import { Controller } from "@hotwired/stimulus";
import { uuid } from "../live/uuid";
import { checkPicture, csrfToken, fileSize, fill, megabytes, nameFromFile, type Sending, sendPicture, storedSize, TYPES } from "../maps/picture_upload";

type Labels = Record<"cancel" | "detail" | "pixels" | "preparing" | "resized" | "sending" | "size" | "stopped" | "type", string>;

// Connects to data-controller="map-picture-upload", on the Criar mapa dialog beside map-scene-picker.
// The Sua imagem step: the master picks a file (or drops one), sees it, names the map and sends it. The upload shows its
// progress, then the server's work, and the new map opens with Configurações open to fit its grid.
export default class extends Controller<HTMLElement> {
	static targets = [
		"input",
		"drop",
		"file",
		"preview",
		"fileName",
		"detail",
		"actions",
		"progress",
		"status",
		"bar",
		"alert",
		"alertText",
		"name",
		"create",
		"kind",
	];
	static values = { url: String, sceneUrl: String, maxBytes: Number, storedSide: Number, labels: Object };
	declare readonly inputTarget: HTMLInputElement;
	declare readonly dropTarget: HTMLElement;
	declare readonly fileTarget: HTMLElement;
	declare readonly previewTarget: HTMLImageElement;
	declare readonly fileNameTarget: HTMLElement;
	declare readonly detailTarget: HTMLElement;
	declare readonly actionsTarget: HTMLElement;
	declare readonly progressTarget: HTMLElement;
	declare readonly statusTarget: HTMLElement;
	declare readonly barTarget: HTMLElement;
	declare readonly alertTarget: HTMLElement;
	declare readonly hasAlertTarget: boolean;
	declare readonly hasInputTarget: boolean;
	declare readonly alertTextTarget: HTMLElement;
	declare readonly nameTarget: HTMLInputElement;
	declare readonly createTarget: HTMLButtonElement;
	declare readonly kindTargets: HTMLInputElement[];
	declare readonly urlValue: string;
	declare readonly sceneUrlValue: string;
	declare readonly maxBytesValue: number;
	declare readonly storedSideValue: number;
	declare readonly labelsValue: Labels;
	file?: File;
	// The scene a file will become, named once per chosen file: a retry after a lost answer lands on the same scene.
	sceneId?: string;
	mutationId?: string;
	// Bumped when the master leaves the step, so an answer to the send they walked away from never opens its scene.
	attempt = 0;
	preview?: string;
	named = false;
	sending?: Sending;
	// Fixed by the first send of this scene id: a lost answer may have created the map, and its grid kind can never change.
	kind?: string;

	disconnect() {
		this.reset();
	}

	browse() {
		this.inputTarget.click();
	}

	choose() {
		this.pick(this.inputTarget.files?.[0]);
	}

	over(event: DragEvent) {
		event.preventDefault();
		this.dropTarget.toggleAttribute("data-over", event.type === "dragover");
	}

	drop(event: DragEvent) {
		event.preventDefault();
		this.dropTarget.removeAttribute("data-over");
		this.pick(event.dataTransfer?.files[0]);
	}

	// A file the name field was never typed into names the map; once the master types there, the field is theirs.
	typed() {
		this.named = true;
	}

	async pick(file: File | undefined) {
		this.inputTarget.value = "";
		if (!file || this.sending) return;
		const failure = checkPicture(file, this.maxBytesValue);
		if (failure) return this.alert(this.failure(failure, file));
		this.clear();
		this.file = file;
		this.sceneId = uuid();
		this.mutationId = uuid();
		this.freezeKind(undefined);
		this.preview = URL.createObjectURL(file);
		this.previewTarget.src = this.preview;
		this.fileNameTarget.textContent = file.name;
		this.detailTarget.textContent = this.describe(file);
		if (!this.named) this.nameTarget.value = nameFromFile(file.name);
		this.showFile(true);
		this.createTarget.disabled = false;
		try {
			await this.previewTarget.decode();
		} catch {
			return;
		}
		if (this.file === file) this.detailTarget.textContent = this.describe(file, this.previewTarget);
	}

	async submit(event: Event) {
		event.preventDefault();
		if (!this.file || this.sending) return;
		const form = new FormData();
		form.set("id", this.sceneId as string);
		form.set("mutation_id", this.mutationId as string);
		form.set("map_scene[background_kind]", "upload");
		form.set("map_scene[name]", this.nameTarget.value.trim());
		this.freezeKind(this.kind ?? this.kindTargets.find((radio) => radio.checked)?.value ?? "square");
		form.set("map_scene[grid][kind]", this.kind as string);
		form.set("map_scene[picture]", this.file);
		this.working(true);
		this.progress(0, this.file.size);
		const attempt = ++this.attempt;
		this.sending = sendPicture({
			url: this.urlValue,
			method: "POST",
			form,
			csrf: csrfToken(),
			onProgress: (loaded, total) => this.progress(loaded, total),
			onSent: () => this.prepare(),
		});
		const answer = await this.sending.done;
		if (attempt !== this.attempt) return;
		this.sending = undefined;
		this.working(false);
		if (answer.kind === "aborted") return;
		if (answer.kind === "failed") return this.alert(this.labelsValue.stopped);
		// A replayed create answers without the scene; the id was ours, so it names the map either way.
		if (answer.status < 300) return Turbo.visit(`${this.sceneUrlValue}/${answer.body?.scene?.id ?? this.sceneId}?configurar=1`);
		// Only a refusal is sure nothing was created; a server or gateway error may come after the map was.
		if (answer.status < 500) this.freezeKind(undefined);
		this.alert(answer.body?.error ?? this.labelsValue.stopped);
	}

	cancel() {
		this.sending?.abort();
	}

	// Voltar: whatever is in flight stops, and its answer is not waited for.
	leave() {
		this.attempt++;
		this.sending?.abort();
		this.sending = undefined;
		if (this.hasInputTarget) this.working(false);
	}

	// Back to the empty step, as the dialog leaves it when it closes.
	reset() {
		this.leave();
		this.clear();
		this.file = undefined;
		this.sceneId = undefined;
		this.mutationId = undefined;
		this.named = false;
		this.hideAlert();
		if (!this.hasInputTarget) return;
		this.nameTarget.value = "";
		this.freezeKind(undefined);
		for (const radio of this.kindTargets) radio.checked = radio.value === "square";
		this.createTarget.disabled = true;
		this.working(false);
		this.showFile(false);
	}

	freezeKind(kind: string | undefined) {
		this.kind = kind;
		for (const radio of this.kindTargets) {
			radio.disabled = kind !== undefined;
			if (kind) radio.checked = radio.value === kind;
		}
	}

	clear() {
		if (this.preview) URL.revokeObjectURL(this.preview);
		this.preview = undefined;
		this.hideAlert();
	}

	failure(failure: "type" | "size", file: File) {
		const { type, size } = this.labelsValue;
		return failure === "type"
			? fill(type, { name: file.name })
			: fill(size, { name: file.name, size: megabytes(file.size, this.locale), limit: megabytes(this.maxBytesValue, this.locale) });
	}

	// "JPEG · 12 MB · 6.000 × 4.000 px, chega como 4.096 × 2.731": the pixels once the browser has read them.
	describe(file: File, image?: HTMLImageElement) {
		const { detail, pixels, resized } = this.labelsValue;
		const number = new Intl.NumberFormat(this.locale);
		let text = fill(detail, { type: TYPES[file.type], size: fileSize(file.size, this.locale) });
		if (!image?.naturalWidth) return text;
		text += fill(pixels, { width: number.format(image.naturalWidth), height: number.format(image.naturalHeight) });
		const stored = storedSize(image.naturalWidth, image.naturalHeight, this.storedSideValue);
		if (stored) text += fill(resized, { width: number.format(stored.width), height: number.format(stored.height) });
		return text;
	}

	progress(loaded: number, total: number) {
		this.statusTarget.textContent = fill(this.labelsValue.sending, { sent: megabytes(loaded, this.locale), total: megabytes(total, this.locale) });
		this.barTarget.style.width = `${total ? Math.min(100, (loaded / total) * 100) : 0}%`;
	}

	prepare() {
		this.statusTarget.textContent = this.labelsValue.preparing;
		this.barTarget.style.width = "100%";
		this.progressTarget.querySelector<HTMLElement>("[data-cancel]")?.setAttribute("hidden", "");
	}

	// The picture stays on screen while it goes; the fields and the buttons wait.
	working(on: boolean) {
		this.progressTarget.hidden = !on;
		this.progressTarget.querySelector<HTMLElement>("[data-cancel]")?.removeAttribute("hidden");
		this.actionsTarget.hidden = on;
		this.nameTarget.disabled = on;
		this.createTarget.disabled = on || !this.file;
	}

	showFile(on: boolean) {
		this.dropTarget.hidden = on;
		this.fileTarget.hidden = !on;
	}

	alert(message: string) {
		this.alertTextTarget.textContent = message;
		this.alertTarget.hidden = false;
	}

	hideAlert() {
		if (this.hasAlertTarget) this.alertTarget.hidden = true;
	}

	get locale() {
		return document.documentElement.lang || "pt-BR";
	}
}
